import { zodResolver } from '@hookform/resolvers/zod';
import type { Timezone } from '@ipfabric/utils/timezones';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type DeepPartialSkipArrayKey,
  type UseFormReturn,
  useFormState,
  useWatch,
} from 'react-hook-form';
import { useCustomForm } from '../utils/hookForm/useCustomForm';
import { buildCronExpression } from './cronBuilder';
import { parseCronExpression } from './cronParser';
import {
  createEmptyOptionStates,
  extractOptionState,
  optionStateToFormData,
} from './cronState';
import type {
  CronEveryOption,
  CronFormData,
  CronFormMeta,
  CronOptionStates,
} from './cronTypes';
import { cronFormSchema } from './cronValidation';

type CronChangeHandler = (
  expression: string,
  timezone: Timezone,
  meta: CronFormMeta,
) => void;

type LastNotifiedState = {
  expression: string;
  timezone: Timezone;
  meta: CronFormMeta;
};

const areMetasEqual = (a: CronFormMeta, b: CronFormMeta): boolean =>
  a.dirty === b.dirty && a.valid === b.valid;

type InitialFormSetup = {
  optionStates: CronOptionStates;
  activeOption: CronEveryOption;
  formData: CronFormData;
};

const createInitialFormSetup = (
  expression: string,
  timezone: Timezone,
): InitialFormSetup => {
  const parsed = parseCronExpression(expression, timezone);
  const activeOption = parsed.every;
  const emptyStates = createEmptyOptionStates();
  const detectedState = extractOptionState(parsed, activeOption);
  const optionStates: CronOptionStates = {
    ...emptyStates,
    [activeOption]: detectedState,
  };

  return {
    optionStates,
    activeOption,
    formData: optionStateToFormData(detectedState, activeOption, timezone),
  };
};

const createDefaultFormSetup = (timezone: Timezone): InitialFormSetup => {
  const emptyStates = createEmptyOptionStates();
  return {
    optionStates: emptyStates,
    activeOption: 'custom',
    formData: optionStateToFormData(emptyStates.custom, 'custom', timezone),
  };
};

export type UseCronFormResult = {
  form: UseFormReturn<CronFormData>;
  every: CronEveryOption;
};

/**
 * Hook manages cron form state and synchronization
 * Initializes RHF instance
 *
 * @param expression - Initial cron expression from parent
 * @param timezone - Initial timezone from parent
 * @param onChange - Callback to notify parent of changes
 * @returns Form control and active 'every' option
 */
export function useCronForm(
  expression: string,
  timezone: Timezone,
  onChange: CronChangeHandler,
): UseCronFormResult {
  const initialSetup =
    expression === ''
      ? createDefaultFormSetup(timezone)
      : createInitialFormSetup(expression, timezone);

  const [optionStates, setOptionStates] = useState<CronOptionStates>(
    initialSetup.optionStates,
  );
  const [activeOption, setActiveOption] = useState<CronEveryOption>(
    initialSetup.activeOption,
  );

  const form = useCustomForm<CronFormData>({
    defaultValues: initialSetup.formData,
    resolver: zodResolver(cronFormSchema),
  });
  const { control, reset } = form;

  const formValues: DeepPartialSkipArrayKey<CronFormData> | undefined =
    useWatch<CronFormData>({ control });
  const { isDirty } = useFormState({ control });

  const getValidFormValues = useCallback((): CronFormData | undefined => {
    if (!formValues) {
      return undefined;
    }

    const result = cronFormSchema.safeParse(formValues);
    if (!result.success) {
      return undefined;
    }

    return result.data;
  }, [formValues]);

  /** Captures the last state sent via onChange to prevent redundant notifications. */
  const lastNotifiedRef = useRef<LastNotifiedState>();

  /**
   * Gets the current active option from tracked state.
   * This reflects the option whose values are stored in optionStates.
   */
  const getActiveOption = useCallback(
    (): CronEveryOption => activeOption,
    [activeOption],
  );

  const saveCurrentOptionState = useCallback(() => {
    const currentOption = getActiveOption();
    if (!formValues) {
      return;
    }

    const newOptionState = extractOptionState(
      formValues as CronFormData,
      currentOption,
    );

    setOptionStates((prev) => ({
      ...prev,
      [currentOption]: newOptionState,
    }));
  }, [getActiveOption, formValues]);

  const switchOption = useCallback(
    (newOption: CronEveryOption) => {
      const currentOption = getActiveOption();
      if (newOption === currentOption) {
        return;
      }

      saveCurrentOptionState();

      const newOptionState = optionStates[newOption];
      const currentTimezone = formValues?.timezone ?? timezone;
      const newFormData = optionStateToFormData(
        newOptionState,
        newOption,
        currentTimezone,
      );

      reset(newFormData);
      setActiveOption(newOption);
    },
    [
      optionStates,
      formValues,
      timezone,
      reset,
      saveCurrentOptionState,
      getActiveOption,
    ],
  );

  // Sync internal form changes to parent (onChange callback)
  // Also handles option switching when 'every' field changes
  useEffect(() => {
    // Check if user changed the 'every' option and switch if needed
    // This happens before building expression to ensure correct option state
    const formEvery = formValues?.every;
    const currentOption = getActiveOption();
    if (formEvery && formEvery !== currentOption) {
      switchOption(formEvery);
      return;
    }

    // Build cron expression from form values and validate
    const validFormValues = getValidFormValues();
    const isValid = validFormValues !== undefined;
    const nextExpression = validFormValues
      ? buildCronExpression(validFormValues)
      : '';
    const nextTimezone = formValues.timezone ?? timezone;
    // Compute meta from latest formState values to ensure we have current dirty state
    const nextMeta: CronFormMeta = {
      dirty: isDirty,
      valid: isValid,
    };

    // Skip notification only if expression, timezone, AND meta are all unchanged
    // This prevents duplicate calls while still allowing meta updates to propagate
    const last = lastNotifiedRef.current;
    if (
      last &&
      last.expression === nextExpression &&
      last.timezone === nextTimezone &&
      areMetasEqual(last.meta, nextMeta)
    ) {
      return;
    }

    // Update last notified state and call parent onChange
    lastNotifiedRef.current = {
      expression: nextExpression,
      timezone: nextTimezone,
      meta: nextMeta,
    };
    onChange(nextExpression, nextTimezone, nextMeta);
  }, [
    formValues,
    switchOption,
    onChange,
    timezone,
    isDirty,
    getActiveOption,
    getValidFormValues,
  ]);

  return {
    form,
    every: getActiveOption(),
  };
}
